import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { consumePasswordReset } from '@/lib/api/auth';
import { ApiError } from '@/lib/api';
import { Button, Card, CardTitle } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { PASSWORD_POLICY_HINT, validatePassword } from '@/lib/passwordPolicy';

/**
 * ResetPassword — target del link de recuperación. El token de reset viaja
 * en la URL (`/reset-password?token=<token>`) y se consume vía
 * `POST /v1/auth/password-reset/consume` (api-core) junto con la pwd nueva.
 */
export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('El link de recuperación no es válido o expiró. Solicitá uno nuevo.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    const pwd = validatePassword(password);
    if (!pwd.ok) {
      setError(pwd.message);
      return;
    }
    setPending(true);
    try {
      await consumePasswordReset(token, password);
      setDone(true);
      window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos actualizar la contraseña.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="container-fc py-12">
      <div className="mx-auto max-w-md">
        <Card variant="default" padded>
          <CardTitle>Nueva contraseña</CardTitle>
          {done ? (
            <>
              <p className="mt-3 text-sm text-fc-ink" role="status">
                Listo. Te redirigimos a iniciar sesión en un momento.
              </p>
              <div className="mt-6">
                <Button as={Link} to="/login" variant="primary">
                  Ir a iniciar sesión
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-fc-muted">
                Definí una contraseña nueva. Tras guardar te llevamos a iniciar sesión.
              </p>
              <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
                <Input
                  label="Contraseña nueva"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  hint={PASSWORD_POLICY_HINT}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Input
                  label="Confirmar contraseña"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                {error && (
                  <p role="alert" className="text-sm text-danger">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={pending}>
                  {pending ? 'Guardando…' : 'Guardar contraseña'}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </section>
  );
}
